'use client';

import React, { useEffect, useRef, useState } from 'react';
import { AnimatedTitle } from '../../animated-title';

import type { LandscapeDocumentary } from '@/types';
import { firstFrame, getFilmData, getMediaData, resizedImageProps } from '@/lib/utils/media.utils';

interface LandscapeDocumentaryProps {
  layout: LandscapeDocumentary;
  /** Shown beside another documentary, each in a card of its own. */
  inRow?: boolean;
  /** The shape the row gives every frame in it (width over height), so its films line up. */
  frame?: number;
}

export const Landscape_Documentary: React.FC<LandscapeDocumentaryProps> = ({ layout, inRow = false, frame }) => {
  const videoUrl = layout.video ? getFilmData(layout.video) : null;
  const image = layout.image ? getMediaData(layout.image) : null;
  const film = useRef<HTMLVideoElement>(null);

  // A vertical film keeps its own shape instead of being cut to a wide frame. Its still tells
  // the shape before the film has loaded; the film confirms it once its header is read, which
  // on a server-rendered page can be before this code is listening: ask, then listen.
  const stillShape = image?.width && image?.height ? image.width / image.height : null;
  const [filmShape, setFilmShape] = useState<number | null>(null);
  const filmAddress = videoUrl?.url;
  useEffect(() => {
    const el = film.current;
    if (!el) return undefined;
    const report = () => {
      if (el.videoWidth && el.videoHeight) setFilmShape(el.videoWidth / el.videoHeight);
    };
    const asked = requestAnimationFrame(report);
    el.addEventListener('loadedmetadata', report);
    return () => {
      cancelAnimationFrame(asked);
      el.removeEventListener('loadedmetadata', report);
    };
  }, [filmAddress]);
  const shape = frame ?? filmShape ?? stillShape;
  const upright = shape !== null && shape < 1;

  return (
    <section className="w-full">
      {/* The film fills the card to its rounded corners; its labels ride on the film's top edge. */}
      <div className="relative mb-[12px] overflow-hidden rounded-md bg-black">
        {layout.title || layout.description ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-4 bg-gradient-to-b from-black/60 to-transparent px-[15px] pb-10 pt-[15px] font-mono text-[10px] text-white">
            {layout.title ? <AnimatedTitle marginClass="mb-0">[ {layout.title} ]</AnimatedTitle> : <span />}
            {layout.description ? (
              <AnimatedTitle marginClass="mb-0" className="text-white/70">
                {layout.description}
              </AnimatedTitle>
            ) : null}
          </div>
        ) : null}

        {videoUrl ? (
          <video
            ref={film}
            // With no still to show, ask for the film's first frame (Safari draws nothing otherwise).
            src={image?.url || !videoUrl.url ? videoUrl.url : firstFrame(videoUrl.url)}
            poster={image?.url}
            controls
            playsInline
            preload="metadata"
            // Reserve the film's frame while loading, with no inset around the rounded video. A
            // wide film gets a 16:9 frame; an upright one its own shape, held to the height of
            // the window when it stands alone.
            className={`block w-full rounded-[inherit] ${
              upright ? (inRow ? 'object-cover' : 'max-h-[88vh] object-contain') : 'aspect-video object-cover'
            }`}
            style={upright && shape ? { aspectRatio: String(shape) } : undefined}
          ></video>
        ) : (
          <div className="group relative flex aspect-video w-full cursor-pointer items-center justify-center">
            {image ? <img {...resizedImageProps(image.url, 1920, '100vw')} alt="Video thumbnail" className="absolute inset-0 h-full w-full object-cover" /> : null}
            <svg
              className="relative z-10 h-16 w-16 text-white transition-transform group-hover:scale-110"
              viewBox="0 0 24 24"
              style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }}
            >
              <path
                fill="currentColor"
                d="M7 6C7 5.07 7.8 4.4 8.67 4.77L20.29 10.12C21.09 10.46 21.09 11.54 20.29 11.88L8.67 17.23C7.8 17.6 7 16.93 7 16V6Z"
              />
            </svg>
          </div>
        )}
      </div>
    </section>
  );
};
